/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { Dialog, DIALOG_SIZE, GridSortDirections } from '@veeam-vspc/components';

import type { PublicCloudEfsBackupRestorePointParam, PublicCloudProtectedVmBackupRestorePointModel } from '@veeam-vspc/models/web-controllers';
import type { CellRendererProps } from '@veeam-vspc/components';

import { AdvancedGrid } from 'views/components/AdvancedGrid';
import { useLang } from 'views/providers/LangProvider/hooks';
import { useAppServices } from 'views/providers/AppProvider/hooks';
import { GridDateColumn } from 'views/components/columns/GridDateColumn';
import { RestorePointSizeColumn } from 'views/components/columns/RestorePointSizeColumn';
import { ToolbarWithExport } from 'views/components/ToolbarWithExport';
import { MAX_INTEGER_32 } from 'core/const';

import type { FsPublicCloudsBackupExtraParams } from '../../interfaces';

export interface FsPublicCloudsBackupDialogProps {
    stateId: string;
    name: string;
    extraParams: FsPublicCloudsBackupExtraParams;
    deactivate: () => void;
}

export const FsPublicCloudsBackupDialog: React.FC<FsPublicCloudsBackupDialogProps> = ({ stateId, name, extraParams, deactivate }) => {
    const lang = useLang();
    const { transportService } = useAppServices();
    const columns = [
        {
            field: 'created',
            title: lang.DATE,
            cell: ({ cellData }: CellRendererProps<PublicCloudProtectedVmBackupRestorePointModel>) => <GridDateColumn value={cellData} />,
        },
        {
            field: 'vmSize',
            title: lang.SIZE,
            cell: ({ cellData }: CellRendererProps<PublicCloudProtectedVmBackupRestorePointModel>) => (
                <RestorePointSizeColumn value={cellData as any} />
            ),
        },
    ];

    return (
        <Dialog
            header={name}
            size={{
                width: DIALOG_SIZE.xl,
                height: DIALOG_SIZE.xl,
            }}
            onRequestClose={deactivate}
            actions={[
                { text: lang.CLOSE, onClick: deactivate },
            ]}
        >
            <AdvancedGrid
                stateId={stateId}
                defaultColumnWidth={100}
                initialState={{
                    sort: {
                        direction: GridSortDirections.Asc,
                        key: columns?.[0]?.field,
                    },
                }}
                columns={columns.filter(item => item)}
                data={(requestParams: PublicCloudEfsBackupRestorePointParam) => transportService
                    .request<typeof requestParams, PublicCloudProtectedVmBackupRestorePointModel[]>(
                        '/PublicCloudEfs/GetProtectedEfsBackupRestorePoints',
                        {
                            ...requestParams,
                            ...extraParams,
                        },
                    )}
                toggleable={false}
                selection={{
                    field: 'instanceUid',
                    checkbox: false,
                    multiple: false,
                }}
                paginationLimit={MAX_INTEGER_32}
                toolbars={[
                    () => (
                        <ToolbarWithExport
                            idProperty={'instanceUid'}
                            extraParams={extraParams}
                            method={'GetPublicCloudEfsRestorePoints'}
                        />
                    ),
                ]}
            />
        </Dialog>
    );
};
